import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api";

const GIS_SRC = "https://accounts.google.com/gsi/client";

// Resolves once the Google Identity Services script is loaded (only loaded once).
function loadGoogleScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  const w = window as any;
  if (w.google?.accounts?.id) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Google script")));
      if (w.google?.accounts?.id) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google script"));
    document.head.appendChild(script);
  });
}

export default function GoogleSignInButton({
  onCredential,
  onError,
}: {
  onCredential: (credential: string) => void;
  onError?: (message: string) => void;
}) {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  // Keep the latest callbacks without re-initializing the Google client.
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await apiFetch("/api/auth/google/config");
        const data = await res.json().catch(() => null);
        const clientId: string | null = data?.clientId || null;
        if (!clientId) {
          if (!cancelled) setEnabled(false);
          return;
        }
        if (cancelled) return;
        setEnabled(true);

        await loadGoogleScript();
        if (cancelled || !buttonRef.current) return;

        const google = (window as any).google;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential?: string }) => {
            if (response?.credential) {
              onCredentialRef.current(response.credential);
            } else {
              onErrorRef.current?.("No credential returned from Google");
            }
          },
        });
        google.accounts.id.renderButton(buttonRef.current, {
          theme: "outline",
          size: "large",
          width: buttonRef.current.clientWidth || 360,
          text: "continue_with",
          logo_alignment: "center",
        });
        if (!cancelled) setReady(true);
      } catch (error: any) {
        if (!cancelled) {
          setEnabled(false);
          onErrorRef.current?.(error?.message || "Unable to load Google sign-in");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Google not configured on the server: render nothing.
  if (enabled === false) return null;

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">or</span>
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <div className="relative min-h-[40px] w-full">
        {!ready && (
          <span className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
            Loading Google sign-in…
          </span>
        )}
        {/* Google owns everything inside this node; React must never add children to it. */}
        <div ref={buttonRef} className="relative flex min-h-[40px] w-full justify-center" />
      </div>
    </div>
  );
}
